import { createLogger } from '@svenskaspelab/nova-core/log';

import {
  BadRequestFileUploadError,
  type IMarketplaceGroupsService,
  type IParseExternalWagerFileUseCase,
} from '../../../domain/entry.js';
import { parseBetPropertyData } from './utils/parseBetPropertyData.js';
import { parseSystem } from './utils/parseSystem.js';

const logger = createLogger('poolbetting.file-upload');

/**
 * Use case for parsing a external wager file.
 *
 * @param marketplaceGroupsService - The service used to fetch marketplace groups
 * @returns Use case executor parsing wager file
 */
export function parseExternalWagerFileUseCase(
  marketplaceGroupsService: IMarketplaceGroupsService
): IParseExternalWagerFileUseCase {
  return {
    execute: async ({ file, product, activatedDrawId }): ReturnType<IParseExternalWagerFileUseCase['execute']> => {
      const arrayBuffer = await file.arrayBuffer();

      const textFile = Buffer.from(arrayBuffer).toString('utf8');

      const lines = textFile.match(/[^\n\r]+/g);

      if (!lines || lines.length < 2) {
        logger.info('File contains wrong amount of rows, expected at least 2 rows');
        throw new BadRequestFileUploadError(
          'File contains wrong amount of rows, expected at least 2 rows',
          undefined,
          'CONTENT_INVALID'
        );
      }

      const [headLine, ...systemLines] = lines;

      if (!headLine) {
        logger.info('File contains no headline');
        throw new BadRequestFileUploadError('File contains no headline', undefined, 'CONTENT_INVALID_HEADER');
      }

      // Do not pass product to parseBetPropertyData, always read product from file
      const betPropertyData = parseBetPropertyData(headLine);
      const systemData = parseSystem(betPropertyData.product, systemLines);

      if (product && product !== betPropertyData.product) {
        logger.info(`Product mismatch, requested: ${product}, actual: ${betPropertyData.product}`);
        throw new BadRequestFileUploadError(
          `Product mismatch, requested: ${product}, actual: ${betPropertyData.product}`,
          undefined,
          'PRODUCT_MISMATCH'
        );
      }

      if (betPropertyData.drawNumber && activatedDrawId) {
        const wallet = await marketplaceGroupsService.getMarketplaceBalanceAsync({
          product: betPropertyData.product,
          drawNumber: betPropertyData.drawNumber,
        });

        if (!wallet.activatedDraws.some((ad) => ad.activatedDrawId === activatedDrawId)) {
          logger.info(`No marketplace wallet for requested AD: ${activatedDrawId}`);
          throw new BadRequestFileUploadError(
            `No marketplace wallet for requested AD: ${activatedDrawId}`,
            undefined,
            'AD_DRAW_MISMATCH'
          );
        }
      }

      return {
        betPropertyData,
        systemData,
      };
    },
  };
}
